/**
 * useChartDrawings.js
 *
 * Custom hook for managing chart drawing state and interactions.
 *
 * WHY THIS EXISTS:
 * - Users need to mark areas on charts to ask the AI Agent about specific patterns
 * - Drawings are stored in data coordinates (timestamp + price) to survive zoom/resize
 * - Hook encapsulates all drawing state, coordinate conversion, and serialization
 *
 * DRAWING TYPES:
 * - pin: Single point marker (click)
 * - circle: Ellipse from drag (2 corners)
 * - arrow: Line with arrowhead (start to end)
 * - rectangle: Box from drag (2 corners)
 * - line: Simple line (start to end)
 * - text: Text annotation at a point (click + type)
 *
 * PERSISTENCE (2026-02-04):
 * - Drawings are persisted to localStorage keyed by storageKey (typically backtestId)
 * - Storage key format: "chart-drawings-{storageKey}"
 * - Drawings reload automatically when storageKey changes
 * - This allows drawings to survive page refresh and view mode changes
 *
 * AI SERIALIZATION:
 * - serializeForAI() returns structured coordinate data for AI analysis
 * - Each drawing type has appropriate bounds/position info
 * - Used by "Explain this trade using AI" button
 */
import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Generate a unique ID for drawings.
 * Uses crypto.randomUUID if available, falls back to timestamp + random.
 */
const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Default drawing color - yellow for visibility against dark chart backgrounds
 */
const DEFAULT_COLOR = '#fbbf24';

/**
 * Get the localStorage key for drawings persistence.
 * @param {string} storageKey - Unique key (e.g., backtestId)
 * @returns {string} Full localStorage key
 */
const getStorageKey = (storageKey) => `chart-drawings-${storageKey}`;

/**
 * Load drawings from localStorage.
 * @param {string} storageKey - Unique key for this chart
 * @returns {Array} Saved drawings or empty array
 */
const loadDrawingsFromStorage = (storageKey) => {
  if (!storageKey) return [];
  try {
    const saved = localStorage.getItem(getStorageKey(storageKey));
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate it's an array
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (err) {
    console.warn('Failed to load drawings from localStorage:', err);
  }
  return [];
};

/**
 * Save drawings to localStorage.
 * @param {string} storageKey - Unique key for this chart
 * @param {Array} drawings - Drawings to save
 */
const saveDrawingsToStorage = (storageKey, drawings) => {
  if (!storageKey) return;
  try {
    localStorage.setItem(getStorageKey(storageKey), JSON.stringify(drawings));
  } catch (err) {
    console.warn('Failed to save drawings to localStorage:', err);
  }
};

/**
 * Hook for managing chart drawings with data coordinate storage.
 *
 * @param {string} storageKey - Optional key for localStorage persistence (e.g., backtestId)
 * @returns {Object} Drawing state and action methods
 */
export function useChartDrawings(storageKey = null) {
  // Core state - initialize from localStorage if storageKey provided
  const [isDrawMode, setIsDrawMode] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [drawings, setDrawings] = useState(() => loadDrawingsFromStorage(storageKey));
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Text annotation state - when user clicks with text tool, we need to show input
  const [pendingTextDrawing, setPendingTextDrawing] = useState(null);

  // Refs to store Recharts scales - updated each render via captureScales
  const scalesRef = useRef({ xScale: null, yScale: null });

  // Persist drawings to localStorage whenever they change
  useEffect(() => {
    if (storageKey && drawings.length >= 0) {
      saveDrawingsToStorage(storageKey, drawings);
    }
  }, [storageKey, drawings]);

  // Reload drawings when storageKey changes (e.g., different backtest selected)
  useEffect(() => {
    if (storageKey) {
      const savedDrawings = loadDrawingsFromStorage(storageKey);
      setDrawings(savedDrawings);
    }
  }, [storageKey]);

  /**
   * Enter draw mode with a specific tool selected.
   * @param {string} tool - Tool type: 'pin', 'circle', 'arrow', 'rectangle', 'line'
   */
  const enterDrawMode = useCallback((tool = 'pin') => {
    setIsDrawMode(true);
    setActiveTool(tool);
  }, []);

  /**
   * Exit draw mode and clear any in-progress drawing.
   * Preserves completed drawings.
   */
  const exitDrawMode = useCallback(() => {
    setIsDrawMode(false);
    setActiveTool(null);
    setCurrentDrawing(null);
    setIsDrawing(false);
    setPendingTextDrawing(null);
  }, []);

  /**
   * Select a different drawing tool while in draw mode.
   * @param {string} tool - Tool type to switch to
   */
  const selectTool = useCallback((tool) => {
    setActiveTool(tool);
    // Cancel any in-progress drawing when switching tools
    setCurrentDrawing(null);
    setIsDrawing(false);
    setPendingTextDrawing(null);
  }, []);

  /**
   * Start a new drawing at the given data coordinates.
   *
   * WHY data coordinates instead of pixels:
   * Recharts X-axis uses a band/point scale for timestamps which doesn't
   * have an invert() method. The caller must extract the timestamp from
   * the mouse event (activePayload/activeLabel) and Y value from yScale.invert().
   *
   * For text tool: Sets pendingTextDrawing instead of starting a drag.
   * User must then call confirmTextDrawing() with the text content.
   *
   * @param {number} timestamp - X data value (timestamp from mouse event)
   * @param {number} value - Y data value (from yScale.invert)
   * @param {number} pixelX - Optional pixel X for positioning text input
   * @param {number} pixelY - Optional pixel Y for positioning text input
   */
  const startDrawing = useCallback((timestamp, value, pixelX = null, pixelY = null) => {
    if (!isDrawMode || !activeTool) return;

    // Validate we have usable coordinates
    if (timestamp === null || timestamp === undefined || value === null || value === undefined) {
      console.warn('useChartDrawings: Invalid coordinates, cannot start drawing');
      return;
    }

    const newDrawing = {
      id: generateId(),
      type: activeTool,
      start: { timestamp, value },
      end: { timestamp, value }, // Same as start initially
      color: DEFAULT_COLOR,
      note: '',
      text: '', // For text annotations
      createdAt: Date.now(),
    };

    // Text tool: Don't start dragging, wait for text input
    if (activeTool === 'text') {
      setPendingTextDrawing({
        ...newDrawing,
        pixelX, // Store pixel position for input overlay
        pixelY,
      });
      return;
    }

    setCurrentDrawing(newDrawing);
    setIsDrawing(true);
  }, [isDrawMode, activeTool]);

  /**
   * Update the in-progress drawing's end point as user drags.
   *
   * @param {number} timestamp - X data value (timestamp from mouse event)
   * @param {number} value - Y data value (from yScale.invert)
   */
  const updateDrawing = useCallback((timestamp, value) => {
    if (!isDrawing || !currentDrawing) return;

    // Validate coordinates
    if (timestamp === null || timestamp === undefined || value === null || value === undefined) {
      return;
    }

    setCurrentDrawing(prev => ({
      ...prev,
      end: { timestamp, value },
    }));
  }, [isDrawing, currentDrawing]);

  /**
   * Complete the current drawing and add it to the drawings array.
   * For pins, start equals end (single click).
   */
  const finishDrawing = useCallback(() => {
    if (!currentDrawing) return;

    setDrawings(prev => [...prev, currentDrawing]);
    setCurrentDrawing(null);
    setIsDrawing(false);
  }, [currentDrawing]);

  /**
   * Confirm a pending text drawing with the entered text content.
   * @param {string} text - The text content for the annotation
   */
  const confirmTextDrawing = useCallback((text) => {
    if (!pendingTextDrawing || !text.trim()) {
      setPendingTextDrawing(null);
      return;
    }

    const textDrawing = {
      ...pendingTextDrawing,
      text: text.trim(),
      pixelX: undefined, // Remove pixel coords from final drawing
      pixelY: undefined,
    };

    setDrawings(prev => [...prev, textDrawing]);
    setPendingTextDrawing(null);
  }, [pendingTextDrawing]);

  /**
   * Cancel a pending text drawing without saving.
   */
  const cancelTextDrawing = useCallback(() => {
    setPendingTextDrawing(null);
  }, []);

  /**
   * Remove a specific drawing by ID.
   * @param {string} id - Drawing ID to remove
   */
  const removeDrawing = useCallback((id) => {
    setDrawings(prev => prev.filter(d => d.id !== id));
  }, []);

  /**
   * Clear all drawings from the chart.
   */
  const clearAllDrawings = useCallback(() => {
    setDrawings([]);
  }, []);

  /**
   * Capture the current Recharts axis scales for coordinate conversion.
   * Called from Customized component during each render.
   *
   * @param {Function} xScale - X-axis scale function with .invert()
   * @param {Function} yScale - Y-axis scale function with .invert()
   */
  const captureScales = useCallback((xScale, yScale) => {
    scalesRef.current = { xScale, yScale };
  }, []);

  /**
   * Get the current scales (for ChartDrawLayer to convert back to pixels).
   * @returns {Object} Current scales { xScale, yScale }
   */
  const getScales = useCallback(() => {
    return scalesRef.current;
  }, []);

  /**
   * Serialize drawings for sending to AI Agent with rich coordinate descriptions.
   * Each drawing type gets appropriate bounds/position info for AI understanding.
   *
   * @returns {Object} Serialized drawings data with detailed coordinate info
   */
  const serializeForAI = useCallback(() => {
    return {
      drawings: drawings.map(d => {
        const startTimestamp = d.start.timestamp;
        const endTimestamp = d.end.timestamp;
        const startValue = d.start.value;
        const endValue = d.end.value;

        // Calculate bounds (left/right for time, top/bottom for price)
        const leftTimestamp = Math.min(startTimestamp, endTimestamp);
        const rightTimestamp = Math.max(startTimestamp, endTimestamp);
        const topPrice = Math.max(startValue, endValue);
        const bottomPrice = Math.min(startValue, endValue);

        // Base serialization with type
        const serialized = {
          type: d.type,
          note: d.note || null,
        };

        switch (d.type) {
          case 'pin':
            // Pin is a single point marker
            serialized.position = {
              datetime: new Date(startTimestamp).toISOString(),
              price: startValue,
            };
            break;

          case 'text':
            // Text annotation at a specific location
            serialized.content = d.text || '';
            serialized.position = {
              datetime: new Date(startTimestamp).toISOString(),
              price: startValue,
            };
            break;

          case 'circle':
            // Circle/ellipse has bounds and center
            serialized.bounds = {
              leftDatetime: new Date(leftTimestamp).toISOString(),
              rightDatetime: new Date(rightTimestamp).toISOString(),
              topPrice: topPrice,
              bottomPrice: bottomPrice,
            };
            serialized.center = {
              datetime: new Date((leftTimestamp + rightTimestamp) / 2).toISOString(),
              price: (topPrice + bottomPrice) / 2,
            };
            break;

          case 'rectangle':
            // Rectangle has bounds
            serialized.bounds = {
              leftDatetime: new Date(leftTimestamp).toISOString(),
              rightDatetime: new Date(rightTimestamp).toISOString(),
              topPrice: topPrice,
              bottomPrice: bottomPrice,
            };
            break;

          case 'arrow':
            // Arrow has from/to points and direction
            serialized.from = {
              datetime: new Date(startTimestamp).toISOString(),
              price: startValue,
            };
            serialized.to = {
              datetime: new Date(endTimestamp).toISOString(),
              price: endValue,
            };
            // Describe the direction
            const timeDiff = endTimestamp - startTimestamp;
            const priceDiff = endValue - startValue;
            let direction = '';
            if (Math.abs(timeDiff) < 1000) { // Less than 1 second = vertical
              direction = priceDiff > 0 ? 'up' : 'down';
            } else if (Math.abs(priceDiff) < 0.01) { // Very small price change = horizontal
              direction = timeDiff > 0 ? 'right' : 'left';
            } else {
              const verticalDir = priceDiff > 0 ? 'up' : 'down';
              const horizontalDir = timeDiff > 0 ? 'right' : 'left';
              direction = `${verticalDir}-${horizontalDir}`;
            }
            serialized.direction = direction;
            break;

          case 'line':
            // Line has from/to points only - no slope description needed
            serialized.from = {
              datetime: new Date(startTimestamp).toISOString(),
              price: startValue,
            };
            serialized.to = {
              datetime: new Date(endTimestamp).toISOString(),
              price: endValue,
            };
            break;

          default:
            // Fallback for unknown types
            serialized.start = {
              datetime: new Date(startTimestamp).toISOString(),
              price: startValue,
            };
            serialized.end = {
              datetime: new Date(endTimestamp).toISOString(),
              price: endValue,
            };
        }

        return serialized;
      }),
    };
  }, [drawings]);

  /**
   * Add a note/annotation to a specific drawing.
   * @param {string} id - Drawing ID
   * @param {string} note - Note text to add
   */
  const updateDrawingNote = useCallback((id, note) => {
    setDrawings(prev => prev.map(d =>
      d.id === id ? { ...d, note } : d
    ));
  }, []);

  /**
   * Update the color of a specific drawing.
   * @param {string} id - Drawing ID
   * @param {string} color - New color value
   */
  const updateDrawingColor = useCallback((id, color) => {
    setDrawings(prev => prev.map(d =>
      d.id === id ? { ...d, color } : d
    ));
  }, []);

  return {
    // State
    isDrawMode,
    activeTool,
    drawings,
    currentDrawing,
    isDrawing,
    pendingTextDrawing,

    // Mode actions
    enterDrawMode,
    exitDrawMode,
    selectTool,

    // Drawing actions
    startDrawing,
    updateDrawing,
    finishDrawing,
    removeDrawing,
    clearAllDrawings,

    // Text annotation actions
    confirmTextDrawing,
    cancelTextDrawing,

    // Scale management
    captureScales,
    getScales,

    // Serialization
    serializeForAI,

    // Editing
    updateDrawingNote,
    updateDrawingColor,
  };
}

export default useChartDrawings;
